import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { fmtMoney, fmtNum } from "@/lib/api";
import { PageHeader, Card, StatusBadge } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import {
  Warehouse, Boxes, Package, TrendingUp, ArrowDownToLine, ArrowUpFromLine,
  IndianRupee, AlertTriangle, Loader2, Wallet,
} from "lucide-react";

function Kpi({ icon: Icon, label, value, sub, tone = "emerald" }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-blue-700",
    stone: "bg-stone-100 text-stone-700",
  };
  return (
    <Card className="p-5 h-full animate-fade-up">
      <div className="flex items-start justify-between">
        <div className={`grid h-10 w-10 place-items-center rounded-lg ${tones[tone]}`}>
          <Icon size={20} />
        </div>
      </div>
      <div className="mt-4 font-display text-3xl font-extrabold tracking-tight text-stone-900">{value}</div>
      <div className="mt-1 text-sm font-medium text-stone-500">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-stone-400">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { canPage } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/reports/dashboard")).data,
  });

  const { data: cashBal } = useQuery({
    queryKey: ["dash-cash"],
    queryFn: async () => (await api.get("/cash/balance")).data,
    enabled: canPage("/cash"),
  });

  if (isLoading || !data)
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="animate-spin text-emerald-700" size={32} />
      </div>
    );

  const { capacity, stock, today, financial, alerts } = data;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Operations Dashboard" subtitle="Live warehouse capacity, stock and financial overview" />

      {canPage("/cash") && (
        <Card className="mb-4 flex flex-col gap-3 border-emerald-200 bg-emerald-50/60 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="dash-cash-widget">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-100 text-emerald-800">
              <Wallet size={20} />
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-emerald-700/80">Manager cash in hand</div>
              <div className="font-display text-2xl font-extrabold text-stone-900">
                {fmtMoney(cashBal?.available_cash_balance ?? 0)}
              </div>
              <div className="text-xs text-stone-500">
                Shared across {cashBal?.warehouse_count ?? "all"} warehouses
              </div>
            </div>
          </div>
          <Link
            to="/cash"
            className="inline-flex h-10 items-center justify-center rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900"
          >
            Open cash register
          </Link>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Warehouse} label="Warehouses" value={capacity.total_warehouses}
             sub={`${capacity.total_stacks} stacks · ${capacity.occupied_stacks} occupied`} />
        <Kpi icon={Package} label="Total Bags in Store" value={fmtNum(stock.total_bags)}
             sub={`${fmtNum(stock.total_qtl)} Qtl · ${stock.active_lots} active lots`} tone="blue" />
        <Kpi icon={Boxes} label="Capacity Utilisation" value={`${capacity.utilisation}%`}
             sub={`${fmtNum(capacity.available_bags)} bags available`} tone="amber" />
        <Kpi icon={IndianRupee} label="Party Outstanding" value={fmtMoney(financial.party_outstanding)}
             sub="Total receivable across parties" tone="stone" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Capacity + today */}
        <Card className="p-5 lg:col-span-1">
          <h3 className="font-display font-semibold text-stone-800 mb-4">Capacity</h3>
          <div className="space-y-3 text-sm">
            <Row label="Total capacity" value={`${fmtNum(capacity.total_capacity_bags)} bags`} />
            <Row label="Occupied" value={`${fmtNum(capacity.occupied_bags)} bags`} />
            <Row label="Available" value={`${fmtNum(capacity.available_bags)} bags`} />
            <div className="pt-2">
              <div className="h-3 w-full rounded-full bg-stone-100 overflow-hidden">
                <div className="h-full bg-emerald-600 transition-all" style={{ width: `${Math.min(capacity.utilisation, 100)}%` }} />
              </div>
              <div className="mt-1 text-xs text-stone-400">{capacity.utilisation}% utilised</div>
            </div>
          </div>

          <h3 className="font-display font-semibold text-stone-800 mt-6 mb-3">Today's Activity</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-emerald-50 p-3">
              <div className="flex items-center gap-1.5 text-emerald-700 text-xs font-semibold"><ArrowDownToLine size={14}/> Inward</div>
              <div className="mt-1 font-display text-2xl font-bold text-stone-900">{today.inward}</div>
              <div className="text-xs text-stone-400">{fmtNum(today.inward_bags)} bags</div>
            </div>
            <div className="rounded-lg bg-blue-50 p-3">
              <div className="flex items-center gap-1.5 text-blue-700 text-xs font-semibold"><ArrowUpFromLine size={14}/> Outward</div>
              <div className="mt-1 font-display text-2xl font-bold text-stone-900">{today.outward}</div>
              <div className="text-xs text-stone-400">{fmtNum(today.outward_bags)} bags</div>
            </div>
          </div>
        </Card>

        {/* Stock breakdown */}
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display font-semibold text-stone-800 mb-4">Stock Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400 mb-2">By Party</div>
              <div className="space-y-2">
                {stock.party_stock.length === 0 && <div className="text-sm text-stone-400">No stock</div>}
                {stock.party_stock.map((p, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
                    <span className="text-sm text-stone-700 truncate">{p.party}</span>
                    <span className="text-sm font-semibold text-stone-900">{fmtNum(p.bags)} bags</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400 mb-2">By Item</div>
              <div className="space-y-2">
                {stock.item_stock.map((it, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
                    <span className="text-sm text-stone-700 truncate">{it.item}</span>
                    <span className="text-sm font-semibold text-stone-900">{fmtNum(it.qtl)} Qtl</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display font-semibold text-stone-800 mb-4 flex items-center gap-2"><TrendingUp size={18}/> Financial Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <FinBox label="Labour Recoverable" value={financial.labour_recoverable} />
            <FinBox label="Dala Recoverable" value={financial.dala_recoverable} />
            <FinBox label="Bag Charges" value={financial.bag_charges} />
            <FinBox label="Party Outstanding" value={financial.party_outstanding} />
            <FinBox label="SRM Expenses" value={financial.srm_expenses} />
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold text-stone-800 mb-4 flex items-center gap-2"><AlertTriangle size={18}/> Alerts</h3>
          <div className="space-y-2">
            {alerts.length === 0 && <div className="text-sm text-stone-400">No active alerts</div>}
            {alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg bg-stone-50 px-3 py-2">
                <StatusBadge tone={a.type}>{a.type}</StatusBadge>
                <span className="text-sm text-stone-700">{a.message}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-stone-500">{label}</span>
    <span className="font-semibold text-stone-900">{value}</span>
  </div>
);

const FinBox = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 p-3">
    <div className="text-xs text-stone-500">{label}</div>
    <div className="mt-1 font-display text-lg font-bold text-stone-900">{fmtMoney(value)}</div>
  </div>
);
